import { zodResolver } from '@hookform/resolvers/zod';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useInlineAttachments } from '@/components/EditableSectionField/_hooks/useInlineAttachments';
import { useProfileMetaData } from '@/components/Hcp/HcpCard/_hooks/useProfileMetaData';
import { Modal } from '@/components/Modal/Modal';
import { modalStyles } from '@/components/Modal/Modal.styles';
import { useAddToast } from '@/components/Snackbar/Snackbar';
import { EntityType, Hcphco } from '@/core/graphql/generated/types';
import { usePostDcrMutation } from '@/features/DataChangeRequest/_graphql/mutation/PostDCR.hook';
import { AttachmentDropzone } from '@/features/DataChangeRequest/Attachments/AttachmentDropzone/AttachmentDropzone';
import { AttachmentGrid } from '@/features/DataChangeRequest/Attachments/AttachmentGrid/AttachmentGrid';
import { ComplianceConfirmation } from '@/features/DataChangeRequest/ReviewModalContent/ComplianceConfirmation/ComplianceConfirmation';
import { ReviewModalNotes } from '@/features/DataChangeRequest/ReviewModalContent/ReviewModalNotes/ReviewModalNotes';

import { profileStatusDcrControlStyles as sx } from './ProfileStatusDcrControl.styles';

type TargetStatus = 'iact' | 'act';

type Props = {
	profile: Hcphco | null;
	entityType: EntityType;
	targetStatus: TargetStatus;
	open: boolean;
	onClose: () => void;
};

const schema = z.object({
	notes: z.string().trim().min(1, { message: 'A note is required.' }),
	isThirdPartyDataConfirmed: z.boolean().refine(val => val === true)
});
type Inputs = z.infer<typeof schema>;

export const ProfileStatusDcrControl = ({
	profile,
	entityType,
	targetStatus,
	open,
	onClose: onCloseProp
}: Props) => {
	const { t } = useTranslation();
	const addToast = useAddToast();
	const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
	const { getProfileName } = useProfileMetaData(profile);

	const titleVerb = targetStatus === 'iact' ? t('Inactivate') : t('Reactivate');
	const successMessage =
		targetStatus === 'iact'
			? t('Profile inactivation submitted for review')
			: t('Profile reactivation submitted for review');
	const bodyText =
		targetStatus === 'iact'
			? t(
					'To help us process this request, please share a brief note on why this profile is being inactivated and any relevant details.'
				)
			: t(
					'To help us process this request, please share a brief note on why this profile is being reactivated and any relevant details.'
				);

	const {
		handleSubmit,
		control,
		formState: { isValid },
		reset: resetForm
	} = useForm<Inputs>({
		resolver: zodResolver(schema),
		defaultValues: {
			notes: '',
			isThirdPartyDataConfirmed: false
		},
		mode: 'onChange'
	});

	const [postDcr, { loading, reset }] = usePostDcrMutation();

	const {
		attachments,
		addFiles,
		removeAttachment,
		attachmentIds,
		discardAll,
		clearLocal
	} = useInlineAttachments(open);

	const onClose = () => {
		if (loading) return;

		onCloseProp();
	};

	const handleExited = () => {
		discardAll();
		setAttachmentsExpanded(false);
		reset();
		resetForm();
	};

	const onSubmit = async (values: Inputs) => {
		const payload =
			entityType === EntityType.Hcp
				? {
						variables: {
							payload: {
								entity: {
									hcp: {
										statusCda_VId: targetStatus,
										vid_V: profile?.vid_V ?? ''
									}
								},
								note: values.notes,
								attachmentIds:
									attachmentIds.length > 0 ? attachmentIds : undefined
							}
						}
					}
				: {
						variables: {
							payload: {
								entity: {
									hco: {
										statusCda_VId: targetStatus,
										vid_V: profile?.vid_V ?? ''
									}
								},
								note: values.notes,
								attachmentIds:
									attachmentIds.length > 0 ? attachmentIds : undefined
							}
						}
					};

		const res = await postDcr(payload);

		if (res.data?.postDcr.__typename === 'DCR') {
			clearLocal();
			addToast(successMessage, undefined, 'success');
			onClose();
		}
	};

	const { name } = useMemo(() => getProfileName({}), [getProfileName]);

	return (
		<Modal
			open={open}
			onExited={handleExited}
			hideCloseButton
			size='sm'
			width={480}
			disableShadow
			disableFooterShadow
			title={`${titleVerb} ${name}`}
			onClose={onClose}
			footer={
				<Box sx={sx.footerRoot}>
					<Box sx={sx.attachmentGroup}>
						<ReviewModalNotes control={control} required />

						<Button
							variant='text'
							size='small'
							onClick={() => setAttachmentsExpanded(true)}
							startIcon={<AttachFileIcon fontSize='small' />}
							sx={sx.addAttachmentButton}
						>
							<Box component='span' data-slot='label'>
								{t('Add attachment')}
							</Box>
							{attachments.length > 0 && (
								<Box component='span' sx={sx.attachmentCount}>
									&nbsp;({attachments.length})
								</Box>
							)}
						</Button>

						{attachmentsExpanded && (
							<AttachmentDropzone
								attachments={attachments}
								addFiles={addFiles}
							/>
						)}

						<AttachmentGrid
							attachments={attachments}
							removeAttachment={removeAttachment}
							size='large'
						/>
					</Box>

					<Divider sx={modalStyles.edgeDivider} />

					<ComplianceConfirmation control={control} />

					<Divider sx={modalStyles.edgeDivider} />

					<Box sx={sx.actions}>
						<Button variant='text' onClick={onClose} disabled={loading}>
							{t('Cancel')}
						</Button>

						<Button
							variant='contained'
							onClick={handleSubmit(onSubmit)}
							loading={loading}
							loadingPosition='end'
							disabled={!isValid}
							sx={sx.submitBtn}
						>
							{t('Submit')}
						</Button>
					</Box>
				</Box>
			}
		>
			<Divider sx={sx.headerDivider} />

			<Typography sx={sx.bodyText}>{bodyText}</Typography>
		</Modal>
	);
};
